import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Stack,
  Button,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  CheckCircle, 
  Cancel, 
  Pending, 
  EmojiEvents,
  ArrowBack,
  Info,
  Visibility
} from '@mui/icons-material';
import { drawService } from '../services/drawService';
import { Draw, DrawWinner, WinnerStatus } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '@mui/material/styles';
import { AppHeader } from '../components/AppHeader';
import { WinnersParticipant } from './participant/WinnersParticipant';

const DrawWinners: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  
  const [draw, setDraw] = useState<Draw | null>(null);
  const [winners, setWinners] = useState<DrawWinner[]>([]);
  const [filteredWinners, setFilteredWinners] = useState<DrawWinner[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState<WinnerStatus | 'ALL'>('ALL');
  
  // Estados para rejeição
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedWinnerId, setSelectedWinnerId] = useState<string | null>(null);
  
  // Estados para visualização de dados
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState<DrawWinner | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const drawData = await drawService.getDrawById(id!);
      
      // Se for USUARIO_FINAL, buscar apenas ganhadores aprovados
      const status = user?.role === 'USUARIO_FINAL' ? 'APPROVED' : undefined;
      const winnersData = await drawService.getDrawWinners(id!, status);
      
      console.log('🎯 Ganhadores carregados:', winnersData);
      console.log('🎯 Status dos ganhadores:', winnersData.map(w => ({ id: w.id, status: w.status })));
      
      // Garantir que todos os ganhadores tenham um status válido
      const winnersWithStatus = winnersData.map(w => ({
        ...w,
        status: w.status || 'PENDING' as WinnerStatus
      }));
      
      setDraw(drawData);
      setWinners(winnersWithStatus);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [id, user?.role]);

  const filterWinners = useCallback(() => {
    if (currentTab === 'ALL') {
      setFilteredWinners(winners);
    } else {
      setFilteredWinners(winners.filter(w => w.status === currentTab));
    }
  }, [currentTab, winners]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    filterWinners();
  }, [filterWinners]);

  const handleApprove = async (winnerId: string) => {
    try {
      await drawService.approveWinner(id!, winnerId);
      await fetchData();
    } catch (error) {
      console.error('Erro ao aprovar ganhador:', error);
      alert('Erro ao aprovar ganhador');
    }
  };

  const handleRejectClick = (winnerId: string) => {
    setSelectedWinnerId(winnerId);
    setShowRejectDialog(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedWinnerId) return;
    
    try {
      await drawService.rejectWinner(id!, selectedWinnerId, rejectReason);
      await fetchData();
      setShowRejectDialog(false);
      setRejectReason('');
      setSelectedWinnerId(null);
    } catch (error) {
      console.error('Erro ao rejeitar ganhador:', error);
      alert('Erro ao rejeitar ganhador');
    }
  };

  const handleViewDetails = (winner: DrawWinner) => {
    setSelectedWinner(winner);
    setShowDetailsDialog(true);
  };

  const getStatusChip = (status: WinnerStatus) => {
    const configs = {
      PENDING: { label: 'Pendente', color: 'warning' as const, icon: <Pending /> },
      APPROVED: { label: 'Aprovado', color: 'success' as const, icon: <CheckCircle /> },
      REJECTED: { label: 'Rejeitado', color: 'error' as const, icon: <Cancel /> }
    };
    
    const config = configs[status] || configs.PENDING; // Fallback para PENDING se status for inválido
    return (
      <Chip 
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  const getTabCount = (status: WinnerStatus | 'ALL') => {
    if (status === 'ALL') return winners.length;
    return winners.filter(w => w.status === status).length;
  };

  // Fluxo do participante (usuário final) tem um visual dedicado — ver
  // frontend/src/pages/participant/WinnersParticipant.tsx. Associação
  // continua na tabela administrativa abaixo, com aprovar/rejeitar.
  if (user?.role === 'USUARIO_FINAL') {
    return <WinnersParticipant draw={draw} winners={filteredWinners} loading={loading} />;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!draw) {
    return (
      <Container>
        <Typography variant="h5" color="error">Sorteio não encontrado</Typography>
      </Container>
    );
  }

  const isAssociation = user?.role === 'ASSOCIACAO';

  return (
    <>
      <AppHeader 
        title={`Ganhadores - ${draw.campaign?.name || 'Sorteio'}`}
        backTo="/draws"
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Informações do Sorteio */}
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {draw.campaign?.name || 'Sorteio'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Prêmio: {draw.prize?.name} • Ganhadores deste sorteio: {draw.maxWinners}
                  {draw.prize ? ` (prêmio total: ${draw.prize.quantity})` : ''}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/draws')}
              >
                Voltar para Sorteios
              </Button>
            </Box>
          </Stack>
        </Paper>

        {/* Tabs de Filtro - Apenas para ASSOCIACAO */}
        {isAssociation && (
          <Paper elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
            <Tabs 
              value={currentTab} 
              onChange={(_, newValue) => setCurrentTab(newValue)}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab 
                label={`Todos (${getTabCount('ALL')})`} 
                value="ALL" 
              />
              <Tab 
                label={`Pendentes (${getTabCount('PENDING')})`} 
                value="PENDING"
                icon={<Pending />}
                iconPosition="start"
              />
              <Tab 
                label={`Aprovados (${getTabCount('APPROVED')})`} 
                value="APPROVED"
                icon={<CheckCircle />}
                iconPosition="start"
              />
              <Tab 
                label={`Rejeitados (${getTabCount('REJECTED')})`} 
                value="REJECTED"
                icon={<Cancel />}
                iconPosition="start"
              />
            </Tabs>
          </Paper>
        )}

        {/* Lista de Ganhadores */}
        {filteredWinners.length === 0 ? (
          <Paper elevation={2} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <EmojiEvents sx={{ fontSize: 80, color: theme.palette.text.secondary, mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Nenhum ganhador encontrado nesta categoria
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Ganhador</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Cupom</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Loja</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Status</TableCell>
                  {isAssociation && (
                    <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">
                      Ações
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredWinners.map((winner) => (
                  <TableRow 
                    key={winner.id}
                    sx={{ 
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {winner.user?.name || 'Nome não disponível'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {winner.user?.email || 'Email não disponível'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {winner.winningCoupon?.number ?? winner.winningCoupon?.code ?? 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {winner.winningCoupon?.store?.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {getStatusChip(winner.status)}
                        {winner.rejectionReason && (
                          <Tooltip title={winner.rejectionReason}>
                            <IconButton size="small">
                              <Info fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    {isAssociation && (
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                          <Tooltip title="Ver dados cadastrais">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDetails(winner)}
                              sx={{ 
                                border: '1px solid',
                                borderColor: 'primary.main',
                                '&:hover': {
                                  backgroundColor: 'primary.light',
                                  color: 'white'
                                }
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {winner.status !== 'APPROVED' && (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircle />}
                              onClick={() => handleApprove(winner.id)}
                              sx={{ minWidth: 100 }}
                            >
                              Aprovar
                            </Button>
                          )}
                          {winner.status !== 'REJECTED' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Cancel />}
                              onClick={() => handleRejectClick(winner.id)}
                              sx={{ minWidth: 100 }}
                            >
                              Rejeitar
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* Dialog de Rejeição */}
      <Dialog 
        open={showRejectDialog} 
        onClose={() => setShowRejectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rejeitar Ganhador</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Informe o motivo da rejeição (opcional):
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Ex: Não seguiu as regras da campanha, cupom inválido, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectDialog(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleRejectConfirm} 
            color="error" 
            variant="contained"
          >
            Confirmar Rejeição
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Dados Cadastrais */}
      <Dialog 
        open={showDetailsDialog} 
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: theme.palette.primary.main, 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Visibility />
          Dados Cadastrais do Ganhador
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedWinner && (
            <Stack spacing={3}>
              {/* Dados Pessoais */}
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: theme.palette.primary.main }}>
                  👤 Dados Pessoais
                </Typography>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Nome Completo:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedWinner.user?.name || 'Não informado'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Email:
                    </Typography>
                    <Typography variant="body1">
                      {selectedWinner.user?.email || 'Não informado'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      CPF:
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedWinner.user?.cpf || 'Não informado'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Telefone:
                    </Typography>
                    <Typography variant="body1">
                      {selectedWinner.user?.phone || 'Não informado'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Localização */}
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: theme.palette.primary.main }}>
                  📍 Localização
                </Typography>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Cidade:
                      </Typography>
                      <Typography variant="body1">
                        {selectedWinner.user?.city || 'Não informado'}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Estado:
                      </Typography>
                      <Typography variant="body1">
                        {selectedWinner.user?.state || 'Não informado'}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Paper>

              {/* Dados do Cupom Vencedor */}
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2, backgroundColor: '#e8f5e9' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: theme.palette.success.main }}>
                  🎟️ Cupom Vencedor
                </Typography>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Número do Cupom:
                    </Typography>
                    <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 700, color: theme.palette.success.main }}>
                      {selectedWinner.winningCoupon?.number || 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Loja:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedWinner.winningCoupon?.store?.name || 'Não informado'}
                    </Typography>
                  </Box>
                  {selectedWinner.winningCoupon?.sellerName && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Vendedor:
                      </Typography>
                      <Typography variant="body1">
                        {selectedWinner.winningCoupon.sellerName}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Data de Cadastro:
                    </Typography>
                    <Typography variant="body1">
                      {selectedWinner.winningCoupon?.createdAt 
                        ? new Date(selectedWinner.winningCoupon.createdAt).toLocaleString('pt-BR')
                        : 'Não informado'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Status do Ganhador */}
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2, backgroundColor: '#fff3e0' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: theme.palette.warning.main }}>
                  📊 Status
                </Typography>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Status Atual:
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {getStatusChip(selectedWinner.status)}
                    </Box>
                  </Box>
                  {selectedWinner.rejectionReason && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Motivo da Rejeição:
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'error.main' }}>
                        {selectedWinner.rejectionReason}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Data do Sorteio:
                    </Typography>
                    <Typography variant="body1">
                      {selectedWinner.winningDate 
                        ? new Date(selectedWinner.winningDate).toLocaleString('pt-BR')
                        : 'Não informado'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setShowDetailsDialog(false)}
            variant="contained"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DrawWinners;
